import { useEffect, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { convertGeneralContextErrorToHookForm } from '@src/common';
import { useAppDispatch, useAppSelector, useTranslate } from '@src/core/hooks';
import { errorsSelector, setResetPasswordCode, setResetPasswordEmail } from '@src/features/customers/auth/store/auth';
import { track } from '@src/services/analytics';

import { usePasswordRecovery } from './usePasswordRecovery';
import {
  recoveryNewPasswordSchema,
  recoveryPasswordFieldSchema,
  type RecoveryNewPasswordFormValues,
} from './recoveryNewPasswordSchema';

export const useRecoveryNewPasswordForm = () => {
  const dispatch = useAppDispatch();
  const { i18n } = useTranslate();
  const contextErrors = useAppSelector(errorsSelector);
  const { changePassword, loading, recoveryEmail, recoveryCode } = usePasswordRecovery();
  const schema = useMemo(() => recoveryNewPasswordSchema(i18n), [i18n]);
  const passwordFieldSchema = useMemo(() => recoveryPasswordFieldSchema(i18n), [i18n]);

  const {
    control,
    setError,
    handleSubmit,
    watch,
    formState: { errors, touchedFields, isSubmitted },
  } = useForm<RecoveryNewPasswordFormValues>({
    mode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: { password: '', passwordConfirmation: '' },
  });

  const password = watch('password') ?? '';
  const passwordConfirmation = watch('passwordConfirmation') ?? '';
  const bothPasswordsFilled = password.length > 0 && passwordConfirmation.length > 0;

  useEffect(() => {
    convertGeneralContextErrorToHookForm('password', setError, contextErrors, 'password_recover');
    convertGeneralContextErrorToHookForm('passwordConfirmation', setError, contextErrors, 'password_recover');
  }, [contextErrors, setError]);

  useEffect(() => {
    track('recoveryPasswordRecoveryStepAccess');
  }, []);

  // Resolver validates both fields from the first typing; here we only decide when each error can appear,
  // because the password is only required after blur and confirmation when filled.
  const visibleErrors: typeof errors = {
    password: touchedFields.password || isSubmitted ? errors.password : undefined,
    passwordConfirmation: bothPasswordsFilled || isSubmitted ? errors.passwordConfirmation : undefined,
  };

  const isSubmitEnabled = schema.safeParse({ password, passwordConfirmation }).success;
  const passwordSuccess = passwordFieldSchema.safeParse(password).success && !errors.password;
  const confirmationSuccess = isSubmitEnabled && !errors.passwordConfirmation;

  const onSubmit = handleSubmit(async (data) => {
    if (!recoveryEmail || !recoveryCode) return;

    track('recoveryPasswordRecoveryStepSubmit');

    const success = await changePassword(recoveryEmail, recoveryCode, data.password);

    if (success) {
      dispatch(setResetPasswordCode(undefined));
      dispatch(setResetPasswordEmail(undefined));
    }
  });

  return {
    control,
    errors: visibleErrors,
    loading,
    isSubmitEnabled,
    onSubmit,
    passwordSuccess,
    confirmationSuccess,
  };
};
